import { ConnectEmailMethod, CookieKey, MasterTranslation, SignUpSource } from '@/constants';
import type { AuthenticateEMSPayload } from '@/fetchers/umsAPI/umsAuthenticator.fetchers';
import { useAuth, useGeo, useGlobalState } from '@/hooks';
import { type IUserInfo, LoginMethod } from '@/interfaces';
import type { LoginReplyFlags } from '@/interfaces/tracking/trackingOnboardingPopup.interfaces';
import { GoogleLoginButton, type GoogleLoginButtonProps } from '@/modules/pmt/components/GoogleLoginButton';
import { EmailSyncGoogleApiScopes } from '@/modules/pmt/constants';
import { useEmailSyncContext } from '@/modules/pmt/context';
import { useLoginCta } from '@/modules/pmt/hooks/useLoginCta.hook';
import { hasGrantedAllScopesGoogle } from '@/modules/pmt/utils';
import { getGaClientId, getSessionId } from '@/modules/pmt/utils/ga4.utils';
import { userInfoModify } from '@/utils';
import { useTrackSignEvents } from '@/utils/GTM/useTrackSignEvents.hook';
import Cookies from 'js-cookie';
import type { FC } from 'react';
import { CTATextContainer, Checkmark, Container, GoogleCTAText, GoogleLoginWrapper } from './LoginEsyncButton.styles';

export interface LoginEsyncButtonProps {
  googleCta?: string;
  handleClick?: GoogleLoginButtonProps['handleClick'];
  handleSuccess: (payload: { loginMethod: LoginMethod; userInfo: IUserInfo } & LoginReplyFlags) => void;
}

export const LoginEsyncButton: FC<LoginEsyncButtonProps> = ({ googleCta, handleClick, handleSuccess }) => {
  const { geoInfo } = useGeo();
  const { t, userInfo, isMultiLanguagePage } = useGlobalState();
  const { authEMS } = useAuth();
  const { setSyncingAccount } = useEmailSyncContext();
  const track = useTrackSignEvents();

  const cta = useLoginCta();

  const loginButtonProps: GoogleLoginButtonProps = {
    onOAuthError: (error) => {
      console.error({ error });
    },
    onOAuthSuccess: async (response) => {
      const { code, scope } = response;

      const systemLoginPayload: AuthenticateEMSPayload = {
        scope,
        code,
        signUpSource: SignUpSource.ConsumerWeb,
        newsletterOptIn: false,
        country: userInfo?.country ?? geoInfo.country,
        gaClientId: getGaClientId(),
        gaSessionId: getSessionId(isMultiLanguagePage),
        eventTimestamp: Date.now().toString(),
        cta,
        authMethod: LoginMethod.Google,
        redirectUri: 'postmessage',
      };

      try {
        // Check if user has granted all access
        const hasAllAccess = hasGrantedAllScopesGoogle(EmailSyncGoogleApiScopes, scope);
        if (!hasAllAccess) Cookies.set(CookieKey.HasGrantedAllAccess, hasAllAccess.toString());

        const res = await authEMS(systemLoginPayload);

        if (res.user_info.email_sync_opt_in) {
          // To show "Syncing in progress banner"
          // Ref: https://www.pivotaltracker.com/story/show/187111995
          setSyncingAccount(code);
          // https://www.pivotaltracker.com/story/show/187112079
          track.connectEmailSuccess({ method: ConnectEmailMethod.LoginCta, emsFirstGrant: res.ems_first_grant });
        }

        // trigger zapier and send GTM
        const modifiedUserInfo = userInfoModify(res.user_info);
        handleSuccess({
          loginMethod: LoginMethod.Google,
          userInfo: modifiedUserInfo,
          created: res.created,
        });
      } catch (e) {
        console.error(e);
      }
    },
    redirect_uri: 'postmessage',
    scope: EmailSyncGoogleApiScopes,
    handleClick,
  };

  return (
    <Container>
      <GoogleLoginWrapper>
        <GoogleLoginButton {...loginButtonProps}>{t(MasterTranslation.ContinueWithGoogle)}</GoogleLoginButton>
      </GoogleLoginWrapper>
      {googleCta && (
        <CTATextContainer>
          <Checkmark alt='Check mark' src='/images/icons/check.svg' width={16} height={16} />
          <GoogleCTAText>{googleCta}</GoogleCTAText>
        </CTATextContainer>
      )}
    </Container>
  );
};
